import type { SearchHit } from "@msb/reader-bridge";
import { defineMessages, useT } from "../i18n/index.ts";
import { Icon } from "../icons/Icon.tsx";

interface Props {
  hits: SearchHit[];
  /** L'occurrence affichée dans le livre. */
  index: number;
  /** Le livre est encore parcouru : d'autres occurrences peuvent arriver. */
  searching?: boolean;
  onStep(index: number): void;
  /** Retour à la liste des résultats. */
  onList(): void;
  onClose(): void;
}

const messages = defineMessages(
  {
    occurrences: "Occurrences de la recherche",
    previous: "Occurrence précédente",
    list: "Liste des résultats",
    searching: "Recherche en cours",
    next: "Occurrence suivante",
    close: "Fermer la recherche",
  },
  {
    occurrences: "Search matches",
    previous: "Previous match",
    list: "List of results",
    searching: "Searching",
    next: "Next match",
    close: "Close search",
  },
);

/** Barre flottante pour passer d'une occurrence à l'autre une fois le panneau de recherche fermé. */
export function SearchTrail({ hits, index, searching = false, onStep, onList, onClose }: Props) {
  const t = useT(messages);
  const hit = hits[index];
  if (!hit) return null;
  return (
    <div class="msb-search-trail" role="group" aria-label={t("occurrences")}>
      <button type="button" class="msb-search-trail__button" aria-label={t("previous")} disabled={index <= 0} onClick={() => onStep(index - 1)}>
        <Icon name="chevronRight" size={22} class="msb-flip" />
      </button>
      <button type="button" class="msb-search-trail__label" aria-label={t("list")} onClick={onList}>
        <span class="msb-search-trail__count">
          {index + 1} / {hits.length}
          {searching && <span class="msb-search-trail__searching" role="status" aria-label={t("searching")} />}
        </span>
        {hit.section && <span class="msb-search-trail__section">{hit.section}</span>}
      </button>
      <button type="button" class="msb-search-trail__button" aria-label={t("next")} disabled={index >= hits.length - 1} onClick={() => onStep(index + 1)}>
        <Icon name="chevronRight" size={22} />
      </button>
      <button type="button" class="msb-search-trail__button" aria-label={t("close")} onClick={onClose}>
        <Icon name="close" size={22} />
      </button>
    </div>
  );
}
